import {
  FeeCalculatorAutoCalculateConfig,
  AnalysisFeeCalculatorDTO,
} from "@models/platform-analysis/entities/steps/fee";
import { AnalysisFeeCalculatorStepConfigs } from "@pages/platform-analysis-v2/_id/steps/fee/calculator/configs";
import {
  extractAutoCalculateVariablesFromConfig,
  calculateFeeCalculatorComparisonValue,
} from "@pages/platform-analysis-v2/_id/steps/fee/calculator/utils";
import { useMemo } from "react";
import { useFormContext, FieldPath, useWatch } from "react-hook-form";
import { useFeeCalculator } from "./useFeeCalculator";

export const useFeeCalculatorAutoCalculatedField = (
  autoCalculateConfig?: FeeCalculatorAutoCalculateConfig,
) => {
  const { control, getValues } = useFormContext<AnalysisFeeCalculatorDTO>();
  const { calculatedTotals, pathsMap } = useFeeCalculator();
  const { sum: sumVariables, subtract: subtractVariables } = useMemo(
    () => extractAutoCalculateVariablesFromConfig(autoCalculateConfig),
    [autoCalculateConfig],
  );

  const watchedSumFieldPaths = useMemo(() => {
    return sumVariables.map(
      (variable) =>
        pathsMap[variable]
          ?.dataValuePath as FieldPath<AnalysisFeeCalculatorDTO>,
    );
  }, [pathsMap, sumVariables]);

  const watchedSubtractFieldPaths = useMemo(() => {
    return subtractVariables.map(
      (variable) =>
        pathsMap[variable]
          ?.dataValuePath as FieldPath<AnalysisFeeCalculatorDTO>,
    );
  }, [pathsMap, subtractVariables]);

  const watchedSumValues = useWatch({
    control,
    name: watchedSumFieldPaths,
  });

  const watchedSubtractValues = useWatch({
    control,
    name: watchedSubtractFieldPaths,
  });

  const calculatedValue = useMemo(() => {
    if (!autoCalculateConfig) return 0;

    const result = calculateFeeCalculatorComparisonValue({
      config: autoCalculateConfig,
      totals: calculatedTotals,
      getValues,
      pathsMap,
    });

    if (typeof result === "boolean") return 0;
    return parseFloat(
      result.toFixed(AnalysisFeeCalculatorStepConfigs.DefaultDecimalPlaces),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoCalculateConfig,
    watchedSumValues,
    calculatedTotals.total,
    watchedSubtractValues,
  ]);

  return useMemo(() => ({ calculatedValue }), [calculatedValue]);
};
